import { useEffect, useState, useRef } from "react"
import { supabase } from "../../lib/supabase"
import { openExternal, openMapsNavigation } from "../../lib/openExternal"
import { useAuth } from "../../contexts/AuthContext"
import useIsMobile from "../../lib/useIsMobile"
import toast from "react-hot-toast"

export default function ProviderGoRequests() {
  const locationWatchRef = useRef(null)
  const { user } = useAuth()
  const isMobile = useIsMobile()
  const [requests, setRequests] = useState([])
  const [mechanics, setMechanics] = useState([])
  const [loading, setLoading] = useState(true)
  const [assigning, setAssigning] = useState(null)
  const [reassigning, setReassigning] = useState(null)
  const [newMechanic, setNewMechanic] = useState("")
  const [selectedMechanic, setSelectedMechanic] = useState("")
  const [timers, setTimers] = useState({})
  const [showPartsRequest, setShowPartsRequest] = useState(null)
  const [nearbyInventory, setNearbyInventory] = useState([])
  const [selectedPart, setSelectedPart] = useState(null)
  const [partQty, setPartQty] = useState(1)
  const [requestingPart, setRequestingPart] = useState(false)
  const [isOnline, setIsOnline] = useState(false)
  const [togglingOnline, setTogglingOnline] = useState(false)

  useEffect(() => {
    if (!user) return
    supabase.from("profiles").select("is_online").eq("id", user.id).maybeSingle().then(({data}) => { if (data) setIsOnline(!!data.is_online) })
    load()
    requestPushPermission()
    const sub = supabase.channel("provider-go-requests")
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"go_service_requests", filter:`provider_id=eq.${user.id}` },
        payload => {
          playAlarm()
          sendPushNotification("🚨 EMERGENCY GO REQUEST!", "Customer needs help urgently. Open CCC now!")
          toast("🚨 NEW EMERGENCY — Customer needs help NOW!", { duration:30000, icon:"🚨", style:{ background:"#e24b4a", color:"#fff", fontWeight:800, fontSize:13 } })
          load()
          let count = 0
          const interval = setInterval(() => {
            count++
            if (count >= 4) { clearInterval(interval); return }
            playAlarm()
          }, 30000)
        })
      .on("postgres_changes", { event:"UPDATE", schema:"public", table:"go_service_requests", filter:`provider_id=eq.${user.id}` },
        () => load())
      .subscribe()
    return () => supabase.removeChannel(sub)
  }, [user])

  useEffect(() => {
    const interval = setInterval(() => {
      setTimers(prev => {
        const updated = { ...prev }
        requests.forEach(r => {
          if (r.status==="pending") {
            const sent = new Date(r.sent_at).getTime()
            const now = Date.now()
            const elapsed = Math.floor((now - sent) / 1000)
            const remaining = Math.max(0, 15*60 - elapsed)
            updated[r.id] = remaining
            if (remaining === 0) handleTimeout(r)
          }
        })
        return updated
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [requests])

  function requestPushPermission() {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission()
    }
  }

  function playAlarm() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      const freqs = [880, 660, 880, 660, 1100]
      freqs.forEach((freq, i) => {
        const o = ctx.createOscillator()
        const g = ctx.createGain()
        o.connect(g); g.connect(ctx.destination)
        o.frequency.value = freq
        o.type = "square"
        g.gain.setValueAtTime(0.4, ctx.currentTime + i*0.25)
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i*0.25 + 0.2)
        o.start(ctx.currentTime + i*0.25)
        o.stop(ctx.currentTime + i*0.25 + 0.2)
      })
    } catch(e) { /* audio playback failed - non-critical */ }
  }

  function sendPushNotification(title, body) {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(title, { body, requireInteraction:true, tag:"go-emergency" })
    }
  }

  function startMechanicTracking(bookingId) {
    if (!navigator.geolocation) return
    if (locationWatchRef.current) navigator.geolocation.clearWatch(locationWatchRef.current)
    locationWatchRef.current = navigator.geolocation.watchPosition(
      async pos => {
        const { latitude, longitude } = pos.coords
        try {
          await supabase.from("mechanic_location_history").insert({
            mechanic_id: user.id,
            booking_id: bookingId,
            latitude,
            longitude,
            recorded_at: new Date().toISOString(),
          })
        } catch(err) { console.error("Mechanic location error:", err) }
      },
      err => console.error("GPS error:", err),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    )
  }

  function stopMechanicTracking() {
    if (locationWatchRef.current) {
      navigator.geolocation.clearWatch(locationWatchRef.current)
      locationWatchRef.current = null
    }
  }

  async function loadNearbyInventory(lat, lng) {
    const { data } = await supabase.from("inventory").select("*, profiles!inventory_provider_id_fkey(first_name,last_name,business_name,latitude,longitude)").eq("is_active", true).gt("stock_quantity", 0)
    if(lat && lng && data) {
      data.forEach(item => {
        const p = item.profiles
        if(p?.latitude && p?.longitude) {
          const dLat = (p.latitude-lat)*Math.PI/180
          const dLng = (p.longitude-lng)*Math.PI/180
          const a = Math.sin(dLat/2)**2+Math.cos(lat*Math.PI/180)*Math.cos(p.latitude*Math.PI/180)*Math.sin(dLng/2)**2
          item._distance = 6371*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a))
        } else { item._distance = 999 }
      })
      data.sort((a,b)=>a._distance-b._distance)
    }
    setNearbyInventory(data||[])
  }
  async function requestPart(bookingId, customerId, address, lat, lng) {
    if(!selectedPart) return
    setRequestingPart(true)
    try {
      const part = nearbyInventory.find(i=>i.id===selectedPart)
      if(!part) throw new Error("Part not found")
      const total = part.price * partQty
      await supabase.from("go_parts_requests").insert({
        booking_id: bookingId, mechanic_id: user.id, inventory_id: part.id,
        provider_id: part.provider_id, quantity: partQty, unit_price: part.price,
        total_amount: total, customer_id: customerId,
        delivery_location_address: address, delivery_location_lat: lat, delivery_location_lng: lng, status: "pending"
      })
      await supabase.from("notifications").insert([
        { user_id: part.provider_id, title: "Part delivery request!", message: "Mechanic needs "+part.name+" x"+partQty+" at "+address+". KES "+total, type: "info" },
        { user_id: customerId, title: "Mechanic needs a part", message: "Your mechanic needs "+part.name+" — KES "+total+". Supplier being contacted.", type: "info" }
      ])
      toast.success("Part request sent!")
      setShowPartsRequest(null); setSelectedPart(null); setPartQty(1)
    } catch(e) { toast.error(e.message) }
    finally { setRequestingPart(false) }
  }
  async function load() {
    const [{ data: reqs }, { data: mechs }] = await Promise.all([
      supabase.from("go_service_requests").select("*, bookings(*)").eq("provider_id", user.id).order("sent_at", { ascending:false }).limit(20),
      supabase.from("mechanics").select("*").eq("provider_id", user.id).eq("is_active", true).eq("is_available", true),
    ])
    setRequests(reqs||[])
    setMechanics(mechs||[])
    setLoading(false)
  }

  async function handleTimeout(request) {
    if (request.status !== "pending") return
    await supabase.from("go_service_requests").update({ status:"timeout", responded_at:new Date().toISOString() }).eq("id", request.id)
    load()
  }

  async function reassignMechanic(bookingId) {
    if(!newMechanic) return toast.error("Please select a mechanic")
    try {
      await supabase.from("bookings").update({ assigned_mechanic_id: newMechanic }).eq("id", bookingId)
      await supabase.from("mechanics").update({ is_available:false, current_booking_id:bookingId }).eq("id", newMechanic)
      const mech = mechanics.find(m=>m.id===newMechanic)
      if(mech?.user_id) {
        await supabase.from("notifications").insert({ user_id:mech.user_id, title:"🚨 GO Service job assigned!", message:"You have been assigned a new emergency GO service job. Check your dashboard.", type:"error" })
      }
      toast.success("Mechanic reassigned!")
      setReassigning(null); setNewMechanic("")
      load()
    } catch(e) { toast.error(e.message) }
  }
  async function acceptRequest(request) {
    if (!selectedMechanic && mechanics.length > 0) return toast.error("Please select a mechanic to dispatch")
    try {
      await supabase.from("go_service_requests").update({ status:"accepted", responded_at:new Date().toISOString() }).eq("id", request.id)
      await supabase.from("bookings").update({
        status: "confirmed",
        provider_accepted_at: new Date().toISOString(),
        assigned_mechanic_id: selectedMechanic||null,
      }).eq("id", request.booking_id)
      if (selectedMechanic) {
        await supabase.from("mechanics").update({ is_available:false, current_booking_id:request.booking_id }).eq("id", selectedMechanic)
      }
      toast.success("Emergency accepted — mechanic dispatched! 🚨")
      // Start GPS tracking for this booking
      startMechanicTracking(request.booking_id)
      const mechanic = mechanics.find(m=>m.id===selectedMechanic)
      const mName = mechanic ? mechanic.first_name+" "+mechanic.last_name : "Our mechanic"
      const mPhone = mechanic?.phone || "Check notifications"
      const mSpec = mechanic?.specialization || "General Mechanic"
      const { data: bkData } = await supabase.from("bookings").select("customer_id").eq("id", request.booking_id).single()
      if (bkData?.customer_id) {
        await supabase.from("notifications").insert({
          user_id: bkData.customer_id,
          title: "🚨 Mechanic dispatched — help is on the way!",
          message: "Mechanic: "+mName+" | "+mSpec+" | Phone: "+mPhone+" | They are heading to your location now. Please stay safe.",
          type: "success"
        })
      }
      // Notify assigned mechanic
      if (selectedMechanic) {
        const { data: mechData } = await supabase.from("mechanics").select("user_id").eq("id", selectedMechanic).single()
        if (mechData?.user_id) {
          await supabase.from("notifications").insert({
            user_id: mechData.user_id,
            title: "🚨 New GO Service job assigned!", 
            message: "Emergency: "+request.bookings?.emergency_type?.replace(/_/g," ")+" at "+request.bookings?.emergency_location_address+". Check your dashboard now!",
            type: "error"
          })
        }
      }
      setAssigning(null)
      setSelectedMechanic("")
      load()
    } catch(err) {
      toast.error(err.message)
    }
  }

  async function declineRequest(request) {
    if (!confirm("Decline this emergency request?")) return
    await supabase.from("go_service_requests").update({ status:"declined", responded_at:new Date().toISOString() }).eq("id", request.id)
    // Clear provider_id first - assign-go-provider refuses to run if the booking still shows
    // an assigned provider, which it would since that's the very provider who just declined
    await supabase.from("bookings").update({ provider_id: null }).eq("id", request.booking_id)
    // Trigger reassignment to the next nearest provider immediately - the timeout cron only
    // catches expired "pending" requests, never explicit declines, so without this the customer
    // would be stuck waiting until manual intervention
    await supabase.functions.invoke("assign-go-provider", { body: { booking_id: request.booking_id } })
    toast.success("Request declined")
    load()
  }

  function formatTime(seconds) {
    if (!seconds && seconds !== 0) return "15:00"
    const m = Math.floor(seconds/60)
    const s = seconds%60
    return `${m}:${s.toString().padStart(2,"0")}`
  }

  const pending = requests.filter(r=>r.status==="pending")
  const recent = requests.filter(r=>r.status!=="pending")

  const EMERGENCY_ICONS = { flat_tire:"🛞", dead_battery:"🔋", out_of_fuel:"⛽", car_wont_start:"🔑", overheating:"🌡️", towing:"🚚", other:"🚗" }

  async function toggleOnline() {
    setTogglingOnline(true)
    const next = !isOnline
    const { error } = await supabase.from("profiles").update({ is_online: next }).eq("id", user.id)
    if (error) { toast.error(error.message) } else { setIsOnline(next); toast.success(next ? "You're online - ready for GO Service requests" : "You're offline") }
    setTogglingOnline(false)
  }
  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:isOnline?"#f0fdf4":"#f5f5f5", border:`1px solid ${isOnline?"#1d9e7540":"#dddddd"}`, borderRadius:12, padding:"0.9rem 1rem", marginBottom:"1.5rem" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width:10, height:10, borderRadius:"50%", background:isOnline?"#1d9e75":"#999", boxShadow:isOnline?"0 0 8px #1d9e75":"none" }}/>
          <div>
            <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, color:isOnline?"#1d9e75":"#555" }}>{isOnline?"Online - receiving GO requests":"Offline"}</div>
            <div style={{ fontSize:11, color:"#888" }}>{isOnline?"Customers can be matched to you":"Go online to start receiving emergency requests"}</div>
          </div>
        </div>
        <button onClick={toggleOnline} disabled={togglingOnline}
          style={{ background:isOnline?"#e24b4a":"#1d9e75", border:"none", borderRadius:8, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:12, fontWeight:700, padding:"9px 16px", cursor:togglingOnline?"not-allowed":"pointer" }}>
          {togglingOnline?"...":isOnline?"Go Offline":"Go Online"}
        </button>
      </div>
      {pending.length > 0 && (
        <div style={{ background:"#fff5f5", border:"2px solid #e24b4a", borderRadius:12, padding:"1rem", marginBottom:"1.5rem" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
            <div style={{ width:10, height:10, borderRadius:"50%", background:"#e24b4a", boxShadow:"0 0 8px #e24b4a" }}/>
            <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:800, color:"#e24b4a" }}>
              🚨 {pending.length} Emergency request{pending.length!==1?"s":""} pending
            </div>
          </div>
          {pending.map(r=>(
            <div key={r.id} style={{ background:"#ffffff", borderRadius:10, padding:"1rem", marginBottom:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                    <span style={{ fontSize:20 }}>{EMERGENCY_ICONS[r.bookings?.emergency_type]||"🚗"}</span>
                    <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, color:"#e24b4a" }}>
                      {r.bookings?.emergency_type?.replace(/_/g," ").toUpperCase()}
                    </div>
                  </div>
                  <div style={{ fontSize:12, color:"#555555", marginBottom:2 }}>📍 {r.bookings?.emergency_location_address}</div>
                  {r.bookings?.emergency_location_lat&&r.bookings?.emergency_location_lng&&(
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${r.bookings.emergency_location_lat},${r.bookings.emergency_location_lng}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ display:"inline-flex", alignItems:"center", gap:6, marginTop:4, marginBottom:4, background:"#4285f4", color:"#fff", borderRadius:8, padding:"6px 12px", fontSize:11, fontWeight:700, textDecoration:"none" }}>
                      🗺️ Navigate to customer
                    </a>
                  )}
                  {r.bookings?.notes&&<div style={{ fontSize:11, color:"#666" }}>📝 {r.bookings.notes}</div>}
                  <div style={{ fontSize:11, color:"#e6821e", marginTop:4 }}>
                    💰 KES {Number(r.bookings?.total_amount||0).toLocaleString()} · Your earnings: KES {Number(r.bookings?.provider_earnings||0).toFixed(0)}
                  </div>
                  <div style={{ fontSize:11, color:"#777777", marginTop:2 }}>Attempt {r.attempt_number} of 5</div>
                </div>
                <div style={{ textAlign:"center", flexShrink:0 }}>
                  <div style={{ fontFamily:"Syne", fontSize:24, fontWeight:800, color:timers[r.id]<60?"#e24b4a":"#e6821e" }}>
                    {formatTime(timers[r.id])}
                  </div>
                  <div style={{ fontSize:9, color:"#777777" }}>time left</div>
                </div>
              </div>

              {assigning===r.id&&mechanics.length>0&&(
                <div style={{ background:"#faf5ff", border:"1px solid #8b5cf630", borderRadius:8, padding:"0.75rem", marginBottom:10 }}>
                  <div style={{ fontSize:11, color:"#8b5cf6", marginBottom:8, fontWeight:600 }}>Select mechanic to dispatch:</div>
                  {mechanics.map(m=>(
                    <div key={m.id} onClick={()=>setSelectedMechanic(m.id)}
                      style={{ display:"flex", alignItems:"center", gap:8, padding:"6px", borderRadius:7, cursor:"pointer", background:selectedMechanic===m.id?"#1e0a3e":"transparent", marginBottom:4 }}>
                      <div style={{ width:28, height:28, borderRadius:"50%", background:"#f0fdf4", border:"1px solid #1d9e7540", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:"#1d9e75" }}>
                        {m.first_name[0]}{m.last_name[0]}
                      </div>
                      <div>
                        <div style={{ fontSize:12, color:"#000000" }}>{m.first_name} {m.last_name}</div>
                        <div style={{ fontSize:10, color:"#777777" }}>{m.specialization}</div>
                      </div>
                      {selectedMechanic===m.id&&<div style={{ marginLeft:"auto", color:"#8b5cf6", fontSize:14 }}>✓</div>}
                    </div>
                  ))}
      {showPartsRequest&&(
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:200, display:"flex", flexDirection:"column", justifyContent:"flex-end" }} onClick={()=>setShowPartsRequest(null)}>
          <div style={{ background:"#fff", borderRadius:"20px 20px 0 0", padding:"1.5rem", maxHeight:"80vh", overflowY:"auto" }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontFamily:"Syne", fontSize:16, fontWeight:800, marginBottom:4 }}>🔧 Request a Part</div>
            <div style={{ fontSize:12, color:"#888", marginBottom:"1rem" }}>Select part from nearby suppliers</div>
            {nearbyInventory.length===0&&<div style={{ fontSize:12, color:"#888", textAlign:"center", padding:"1rem" }}>No parts available nearby</div>}
            {nearbyInventory.map(item=>(
              <div key={item.id} onClick={()=>setSelectedPart(item.id)} style={{ background:selectedPart===item.id?"#f3f0ff":"#f8f8f8", border:"1px solid "+(selectedPart===item.id?"#8b5cf6":"#eee"), borderRadius:10, padding:"0.75rem", marginBottom:8, cursor:"pointer" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div><div style={{ fontSize:13, fontWeight:600 }}>{item.name}</div><div style={{ fontSize:11, color:"#888" }}>{item.profiles?.business_name||item.profiles?.first_name} · {item._distance<999?item._distance.toFixed(1)+"km":""}</div><div style={{ fontSize:11, color:"#555" }}>Stock: {item.stock_quantity}</div></div>
                  <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:800, color:"#8b5cf6" }}>KES {Number(item.price).toLocaleString()}</div>
                </div>
              </div>
            ))}
            {selectedPart&&(
              <div style={{ marginTop:12 }}>
                <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:12 }}>
                  <button onClick={()=>setPartQty(q=>Math.max(1,q-1))} style={{ width:36, height:36, borderRadius:8, border:"1px solid #ddd", background:"#f5f5f5", fontSize:18, cursor:"pointer" }}>-</button>
                  <span style={{ fontFamily:"Syne", fontSize:18, fontWeight:800, minWidth:30, textAlign:"center" }}>{partQty}</span>
                  <button onClick={()=>setPartQty(q=>q+1)} style={{ width:36, height:36, borderRadius:8, border:"1px solid #ddd", background:"#f5f5f5", fontSize:18, cursor:"pointer" }}>+</button>
                  <span style={{ fontSize:12, color:"#888" }}>KES {((nearbyInventory.find(i=>i.id===selectedPart)?.price||0)*partQty).toLocaleString()}</span>
                </div>
                <button onClick={()=>{ const r=requests.find(x=>x.booking_id===showPartsRequest); requestPart(showPartsRequest, r?.bookings?.customer_id, r?.bookings?.emergency_location_address, r?.bookings?.emergency_location_lat, r?.bookings?.emergency_location_lng) }} disabled={requestingPart} style={{ width:"100%", background:requestingPart?"#ccc":"#8b5cf6", border:"none", borderRadius:10, color:"#fff", fontFamily:"Syne", fontSize:14, fontWeight:700, padding:"13px", cursor:requestingPart?"not-allowed":"pointer" }}>{requestingPart?"Sending...":"Send Part Request"}</button>
              </div>
            )}
            <button onClick={()=>setShowPartsRequest(null)} style={{ width:"100%", background:"none", border:"1px solid #ddd", borderRadius:10, color:"#666", fontSize:13, padding:"11px", cursor:"pointer", marginTop:8 }}>Cancel</button>
          </div>
        </div>
      )}
                </div>
              )}

              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <button onClick={()=>{ setAssigning(assigning===r.id?null:r.id); setSelectedMechanic("") }}
                  style={{ background:"#e24b4a", border:"none", borderRadius:8, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:12, fontWeight:700, padding:"9px 18px", cursor:"pointer" }}>
                  {assigning===r.id?"Cancel":"🚨 Accept & dispatch"}
                </button>
                {assigning===r.id&&(
                  <button onClick={()=>acceptRequest(r)} disabled={mechanics.length>0&&!selectedMechanic}
                    style={{ background:mechanics.length>0&&!selectedMechanic?"#555555":"#1d9e75", border:"none", borderRadius:8, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:12, fontWeight:700, padding:"9px 18px", cursor:"pointer" }}>
                    Confirm dispatch
                  </button>
                )}
                <button onClick={()=>declineRequest(r)}
                  style={{ background:"none", border:"1px solid #e24b4a40", borderRadius:8, color:"#e24b4a", fontSize:12, padding:"9px 14px", cursor:"pointer" }}>
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, color:"#000000", marginBottom:"1rem" }}>
        Recent GO requests
      </div>

      {loading&&<div style={{ color:"#777777", fontSize:13 }}>Loading...</div>}
      {!loading&&recent.length===0&&pending.length===0&&(
        <div style={{ color:"#888888", fontSize:13, textAlign:"center", padding:"2rem" }}>
          <div style={{ fontSize:32, marginBottom:10 }}>🚨</div>
          No GO service requests yet
        </div>
      )}

      {recent.map(r=>(
        <div key={r.id} style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:10, padding:"0.9rem", marginBottom:8 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <span>{EMERGENCY_ICONS[r.bookings?.emergency_type]||"🚗"}</span>
                <span style={{ fontSize:13, color:"#000000" }}>{r.bookings?.emergency_type?.replace(/_/g," ")}</span>
                <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:r.status==="accepted"?"#f0fdf4":r.status==="declined"?"#fff5f5":"#ffffff", color:r.status==="accepted"?"#1d9e75":r.status==="declined"?"#e24b4a":"#888" }}>{r.status}</span>
              </div>
              <div style={{ fontSize:11, color:"#777777" }}>📍 {r.bookings?.emergency_location_address}</div>
              {r.bookings?.emergency_location_lat&&r.bookings?.emergency_location_lng&&(
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${r.bookings.emergency_location_lat},${r.bookings.emergency_location_lng}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ display:"inline-flex", alignItems:"center", gap:6, marginTop:4, marginBottom:4, background:"#4285f4", color:"#fff", borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, textDecoration:"none" }}>
                  🗺️ Navigate to customer
                </a>
              )}
              <div style={{ fontSize:10, color:"#888888", marginTop:2 }}>{new Date(r.sent_at).toLocaleString()}</div>
              {r.bookings?.status==="in-progress"&&<button onClick={()=>{ setShowPartsRequest(r.booking_id); loadNearbyInventory(r.bookings?.emergency_location_lat, r.bookings?.emergency_location_lng) }} style={{ marginTop:8, background:"#8b5cf6", border:"none", borderRadius:8, color:"#fff", fontSize:11, fontWeight:700, padding:"6px 14px", cursor:"pointer" }}>🔧 Request Part</button>}
              {r.status==="accepted"&&r.bookings?.status!=="in-progress"&&(
                reassigning===r.booking_id ? (
                  <div style={{ marginTop:8 }}>
                    <select value={newMechanic} onChange={e=>setNewMechanic(e.target.value)} style={{ width:"100%", padding:"6px 10px", borderRadius:8, border:"1px solid #ddd", fontSize:12, marginBottom:6 }}>
                      <option value="">Select mechanic</option>
                      {mechanics.map(m=><option key={m.id} value={m.id}>{m.first_name} {m.last_name} · {m.specialization}</option>)}
                    </select>
                    <div style={{ display:"flex", gap:6 }}>
                      <button onClick={()=>reassignMechanic(r.booking_id)} style={{ flex:1, background:"#378add", border:"none", borderRadius:8, color:"#fff", fontSize:11, fontWeight:700, padding:"6px 10px", cursor:"pointer" }}>✓ Confirm</button>
                      <button onClick={()=>{ setReassigning(null); setNewMechanic("") }} style={{ flex:1, background:"#f0f0f0", border:"none", borderRadius:8, color:"#555", fontSize:11, padding:"6px 10px", cursor:"pointer" }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={()=>setReassigning(r.booking_id)} style={{ marginTop:8, background:"#378add", border:"none", borderRadius:8, color:"#fff", fontSize:11, fontWeight:700, padding:"6px 14px", cursor:"pointer" }}>👨‍🔧 Reassign Mechanic</button>
                )
              )}
              </div>
            <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, color:"#e6821e" }}>
              KES {Number(r.bookings?.total_amount||0).toLocaleString()}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}






